<script setup lang="ts">
import FinestInputText from '@/components/FinestInputText.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import type { FieldState } from '@/utils/field-state'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    options: { label: string; value: string }[]
    placeholder?: string
    textPlaceholder?: string
    ariaLabel?: string
    disabled?: boolean
    state?: FieldState | null
  }>(),
  {
    placeholder: '',
    textPlaceholder: '',
    disabled: false,
    state: null,
  },
)

const model = defineModel<string | null>({ default: null })

const emit = defineEmits<{
  (event: 'change', value: string | null): void
}>()

const { t } = useI18n({ useScope: 'local' })

const CUSTOM = '__custom__'

const isCustomValue = (value: string | null): boolean =>
  Boolean(value) && !props.options.some((option) => option.value === value)

const custom = ref(isCustomValue(model.value))

watch(
  () => model.value,
  (value) => {
    if (isCustomValue(value)) {
      custom.value = true
    }
  },
)

const selectOptions = computed(() => [...props.options, { label: t('custom'), value: CUSTOM }])

const selectValue = computed(() => (custom.value ? CUSTOM : model.value))

const text = computed(() => (custom.value ? (model.value ?? '') : ''))

function chooseOption(value: string | null): void {
  if (value === CUSTOM) {
    custom.value = true

    if (model.value !== null && !isCustomValue(model.value)) {
      commit(null)
    }

    return
  }

  custom.value = false
  commit(value)
}

function commit(value: string | null): void {
  const trimmed = value?.trim() ?? ''

  model.value = trimmed === '' ? null : trimmed
  emit('change', model.value)
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <FinestSelect
      :model-value="selectValue"
      :options="selectOptions"
      :placeholder="placeholder"
      :aria-label="ariaLabel"
      :disabled="disabled"
      :state="state"
      class="text-left"
      @change="chooseOption" />

    <FinestInputText
      v-if="custom"
      :model-value="text"
      :placeholder="textPlaceholder || t('customPlaceholder')"
      :aria-label="ariaLabel"
      :disabled="disabled"
      :state="state"
      @keydown.enter.prevent="commit(($event.target as HTMLInputElement).value)"
      @blur="commit(($event.target as HTMLInputElement).value)" />
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "custom": "Sonstiges",
    "customPlaceholder": "Eigene Angabe"
  },
  "en": {
    "custom": "Other",
    "customPlaceholder": "Own entry"
  }
}
</i18n>
